/** @format */

import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormHelperText,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FormikProps, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { DependantsDto } from '../../Models';
import GuardianTypeOptions from '../../../../common/dropdowns/GuardianTypeOptions';

interface Props {
  formik: FormikProps<any>;
}
export default function AddDependants({ formik }: Props) {
  const [houseHoldIncomeDepandants, setHouseHoldIncomeDepandants] =
    useState<number>(0);
  // Index of the dependant currently being edited inline. When set we replace
  // the dependant at that index on submit (instead of appending) and the
  // matching chip is highlighted with its delete control disabled.
  const [editingDependantIndex, setEditingDependantIndex] = useState<
    number | null
  >(null);

  const validationSchema = yup.object({
    name: yup.string().required('Please enter your relatives name').min(3),
    guardianTypeOptionId: yup
      .number()
      .required('Please enter relationship')
      .min(1, 'Please enter relationship'),
  });
  const blankDependant: DependantsDto = {
    name: '',
    guardianTypeOptionId: 0,
    id: 0,
  };
  const formikAddDependents: FormikProps<DependantsDto> =
    useFormik<DependantsDto>({
      initialValues: blankDependant,
      validationSchema: validationSchema,
      onSubmit: (values) => {
        const dependents = [...formik.values.dependants];
        if (editingDependantIndex !== null && editingDependantIndex >= 0) {
          dependents[editingDependantIndex] = values;
          setEditingDependantIndex(null);
        } else {
          dependents.push(values);
        }
        formik.setFieldValue('dependants', dependents);
        formikAddDependents.resetForm({ values: blankDependant });
      },
    });

  useEffect(() => {
    setHouseHoldIncomeDepandants(formik.values.houseHoldIncomeDepandants);
  }, [formik.values.houseHoldIncomeDepandants]);

  const isEditing = editingDependantIndex !== null;

  const showForm = () => {
    // Always show the form while editing (so the user can complete the edit
    // even when the household count is already fully populated).
    if (isEditing) return true;
    if (houseHoldIncomeDepandants === formik.values.dependants.length)
      return false;
    if (houseHoldIncomeDepandants > formik.values.dependants.length)
      return true;

    return false;
  };

  const handleDelete = (index: number) => {
    const dependents = [...formik.values.dependants];
    if (index >= 0 && index < dependents.length) {
      dependents.splice(index, 1);
      formik.setFieldValue('dependants', dependents);
      // If we just deleted the row we were editing, drop out of edit mode.
      if (editingDependantIndex === index) {
        formikAddDependents.resetForm({ values: blankDependant });
        setEditingDependantIndex(null);
      } else if (
        editingDependantIndex !== null &&
        index < editingDependantIndex
      ) {
        // Shift the editing index left so we still point at the same row.
        setEditingDependantIndex(editingDependantIndex - 1);
      }
    }
  };

  const handleEdit = (index: number) => {
    const target = formik.values.dependants[index];
    if (!target) return;
    formikAddDependents.resetForm({
      values: { ...blankDependant, ...target },
    });
    setEditingDependantIndex(index);
  };

  const handleCancelEdit = () => {
    formikAddDependents.resetForm({ values: blankDependant });
    setEditingDependantIndex(null);
  };

  const handleCount = () => {
    const household = parseInt(houseHoldIncomeDepandants.toString());
    if (formik.values.dependants.length === household) return household;

    return formik.values.dependants.length + 1;
  };

  return (
    <>
      {houseHoldIncomeDepandants > 0 && (
        <Box className='add__dependents_container'>
          <Grid container spacing={2}>
            {houseHoldIncomeDepandants > 0 && (
              <Grid item xs={12}>
                <Typography variant='body1' color='CaptionText'>
                  Household depedents
                  <b>{` ${handleCount()} of ${houseHoldIncomeDepandants}`}</b>
                </Typography>
                {showForm() && (
                  <FormHelperText>
                    Please add dependents in the below form
                  </FormHelperText>
                )}
              </Grid>
            )}

            <Grid item xs={12} mb={3}>
              <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap' }}>
                {formik.values.dependants.map(
                  (data: DependantsDto, key: React.Key) => {
                    const idx = key as number;
                    const isRowEditing = editingDependantIndex === idx;
                    return (
                      <Chip
                        size='medium'
                        key={key}
                        label={data.name}
                        clickable={!isRowEditing}
                        color={isRowEditing ? 'warning' : 'default'}
                        icon={
                          isRowEditing ? (
                            <EditIcon fontSize='small' />
                          ) : undefined
                        }
                        title={
                          isRowEditing
                            ? 'Editing this dependant in the form below'
                            : 'Click to edit this dependant'
                        }
                        onClick={
                          isRowEditing ? undefined : () => handleEdit(idx)
                        }
                        onDelete={
                          isRowEditing ? undefined : () => handleDelete(idx)
                        }
                      />
                    );
                  }
                )}
              </Stack>
              {isEditing && (
                <Box mt={1}>
                  <Alert severity='warning' variant='standard'>
                    You are editing a dependant. Click <b>Update Dependent</b>{' '}
                    to save your changes or <b>Cancel</b> to discard them.
                  </Alert>
                </Box>
              )}
            </Grid>

            {showForm() && (
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  required
                  id='name'
                  name='name'
                  label='Enter Name'
                  value={formikAddDependents.values.name}
                  onChange={formikAddDependents.handleChange}
                  onBlur={formikAddDependents.handleBlur}
                  type='text'
                  error={
                    formikAddDependents.touched.name &&
                    Boolean(formikAddDependents.errors.name)
                  }
                  helperText={
                    formikAddDependents.touched.name &&
                    (formikAddDependents.errors.name as React.ReactNode)
                  }
                />
              </Grid>
            )}

            {showForm() && (
              <Grid item xs={12} md={5}>
                <GuardianTypeOptions
                  formik={formikAddDependents}
                  disableParents={true}
                />
              </Grid>
            )}

            {showForm() && (
              <Grid item xs={12} md={2}>
                <Stack direction='column' spacing={1}>
                  <Button
                    fullWidth
                    variant='contained'
                    color={isEditing ? 'warning' : 'info'}
                    size='large'
                    sx={{ height: isEditing ? 40 : 55 }}
                    onClick={() => formikAddDependents.submitForm()}>
                    {isEditing ? 'Update Dependent' : 'Add Dependent'}
                  </Button>
                  {isEditing && (
                    <Button
                      fullWidth
                      variant='outlined'
                      color='inherit'
                      size='small'
                      onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  )}
                </Stack>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </>
  );
}
